import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import {
  IFormationLayout,
  ISoccerMatchCardEvent,
  ISoccerMatchEvent,
  ISoccerMatchEventWithPenaltyGroup,
  ISoccerMatchGoalEvent,
  ISoccerMatchSubstituteEvent,
  SoccerMatchEventType,
} from "../../../types";
import { getColorFromConfig } from "../config/tournament";
import { useFixtureScore } from "../stores";
import { useAppStore } from "../stores/useAppStore";
import { generateFormationLayout } from "../utils";
import { useMatchLineups } from "./useMatchLineups";

export type ISoccerMatchEventWithPlayerAction = {
  playerAction?: string;
} & (ISoccerMatchEvent | ISoccerMatchEventWithPenaltyGroup);

export interface IGroupedPlayerData {
  events: ISoccerMatchEventWithPlayerAction[];
  playerId: string;
  playerName: string;
}

export interface IGroupedPlayers {
  [id: string]: IGroupedPlayerData;
}

export interface IGroupedData {
  contestantId: string;
  players: IGroupedPlayers;
}

export function useLineUpsData() {
  const matchId = useFixtureScore(useShallow(({ match }) => match?.id));
  const { data, error, isLoading, refetch } = useMatchLineups(matchId);

  const [selectedTab, setSelectedTab] = useState<"home" | "away">("home");
  const [layout, setLayout] = useState<IFormationLayout[]>([]);

  const matchData = useAppStore((state) => state.matchData);
  const eventsData = matchData?.events || [];

  // Group events by contestant and then by player
  const groupedData = useMemo(() => {
    const grouped = {} as Record<string, IGroupedData>;

    eventsData.forEach((event) => {
      // Initialize contestant if not exists
      if (!grouped[event.contestantId]) {
        grouped[event.contestantId] = {
          contestantId: event.contestantId,
          players: {},
        };
      }

      // For substitute events, handle both playerOn and playerOff
      if (event.eventType === SoccerMatchEventType.SUSTITUTE) {
        // Player coming on
        const substituteEvent = event as ISoccerMatchSubstituteEvent;
        if (substituteEvent.playerOnId && substituteEvent.playerOnName) {
          if (
            !grouped[substituteEvent.contestantId].players[
              substituteEvent.playerOnId
            ]
          ) {
            grouped[substituteEvent.contestantId].players[
              substituteEvent.playerOnId
            ] = {
              playerId: substituteEvent.playerOnId,
              playerName: substituteEvent.playerOnName,
              events: [],
            };
          }
          grouped[substituteEvent.contestantId].players[
            substituteEvent.playerOnId
          ].events.push({
            ...substituteEvent,
            playerAction: "substitute_on",
          });
        }

        // Player going off
        if (substituteEvent.playerOffId && substituteEvent.playerOffName) {
          if (
            !grouped[substituteEvent.contestantId].players[
              substituteEvent.playerOffId
            ]
          ) {
            grouped[substituteEvent.contestantId].players[
              substituteEvent.playerOffId
            ] = {
              playerId: substituteEvent.playerOffId,
              playerName: substituteEvent.playerOffName,
              events: [],
            };
          }
          grouped[substituteEvent.contestantId].players[
            substituteEvent.playerOffId
          ].events.push({
            ...event,
            playerAction: "substitute_off",
          });
        }
      } else {
        // For other events, use playerId or scorerId
        const goalEvent = event as ISoccerMatchGoalEvent;
        const otherEvent = event as ISoccerMatchCardEvent;

        const playerId = otherEvent.playerId || goalEvent.scorerId;
        const playerName = otherEvent.playerName || goalEvent.scorerName;

        if (playerId && playerName) {
          if (!grouped[event.contestantId].players[playerId]) {
            grouped[event.contestantId].players[playerId] = {
              playerId: playerId,
              playerName: playerName,
              events: [],
            };
          }
          grouped[event.contestantId].players[playerId].events.push(event);
        }
      }
    });

    // Sort events by time for each player
    Object.values(grouped).forEach((contestant) => {
      Object.values(contestant.players).forEach((player) => {
        player.events.sort((a, b) => a.timeMin - b.timeMin);
      });
    });

    return grouped;
  }, [eventsData]);

  const teamAdata = data.teamA;
  const teamBdata = data.teamB;
  const teamAName = data.teamA?.teamName || "";
  const teamBName = data.teamB?.teamName || "";
  let teamBShirtColor = "";
  let teamBTextColor = "";

  const { shirtColor: teamAShirtColor, textColor: teamATextColor } =
    getColorFromConfig(teamAName);
  const {
    shirtColor: initialTeamBShirtColor,
    textColor: initialTeamBTextColor,
  } = getColorFromConfig(teamBName, true);

  teamBShirtColor = initialTeamBShirtColor || "";
  teamBTextColor = initialTeamBTextColor || "";

  // if both the teams have same color then change away team to use home color
  if (teamAShirtColor === teamBShirtColor) {
    const colorData = getColorFromConfig(teamBName);
    teamBShirtColor = colorData.shirtColor || "";
    teamBTextColor = colorData.textColor || "";
  }

  const currentTeamData = selectedTab === "home" ? teamAdata : teamBdata;
  const lineups = currentTeamData.players;
  const teamOfficials = currentTeamData.teamOfficials;
  const teamTextColor =
    selectedTab === "home" ? teamATextColor : teamBTextColor;
  const teamShirtColor =
    selectedTab === "home" ? teamAShirtColor : teamBShirtColor;

  useEffect(() => {
    const formation = currentTeamData.formationUsed;
    setLayout(generateFormationLayout(formation));
  }, [selectedTab, teamAdata.formationUsed, teamBdata.formationUsed]);

  return {
    // Data
    teamAdata,
    teamBdata,
    lineups,
    layout,
    formationUsed: currentTeamData.formationUsed,
    teamOfficials,

    // Colors
    teamTextColor,
    teamShirtColor,
    teamAShirtColor,
    teamBShirtColor,
    teamATextColor,
    teamBTextColor,

    // State
    selectedTab,
    setSelectedTab,
    currentGroupedData: groupedData[currentTeamData?.contestantId || ""],

    // Status
    isLoading,
    error,
    refetch,
  };
}
